import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Paper, Slide } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNotifications } from '../../../../hooks/useNotifications';
import { useUser } from '../../../../context/UserContext';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import NotificationService from '../../../../services/NotificationService';
import QueryResultDialog from '../../../../pages/Clients/QueryBuilder/components/QueryResultDialog';
import WorkspaceRedirectDialog from './NotificationBell/WorkspaceRedirectDialog';
import {
  NotificationBellButton,
  NotificationPanelHeader,
  NotificationList,
} from './NotificationBell/index';

const normalizeWorkspaceId = (value: string): string => value.trim().toLowerCase();

const NotificationBell = () => {
  const { userInfos, workspaces, selectedWorkspace, setSelectedWorkspace } = useUser();
  const userId = userInfos?.userId || undefined;
  const { topbarTheme } = useLayoutPreferences();
  const theme = useTheme();
  const highlightColor = theme.palette.primary.main;

  const { notifications, unreadCount, markAsRead, archiveNotification, isRead, isLoading } =
    useNotifications(userId);

  const [panelOpen, setPanelOpen]               = useState(false);
  const [tabValue, setTabValue]                 = useState(0);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [resultQueryId, setResultQueryId]       = useState<string | number | null>(null);
  // Set only while waiting on the user's answer to "you're about to be switched to another
  // workspace" — holds what to do once they accept, since the target workspace isn't necessarily
  // the current one (the dialog never opens for a notification on the current workspace already).
  const [pendingRedirect, setPendingRedirect]   = useState<{ queryId: string; targetWorkspaceName: string } | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const bellRef  = useRef<HTMLButtonElement>(null);

  const unreadNotifications = useMemo(
    () => notifications.filter(n => !isRead(n.id)),
    [notifications],
  );

  const togglePanel    = useCallback(() => setPanelOpen(v => !v), []);
  const closePanel     = useCallback(() => setPanelOpen(false), []);
  const markAllAsRead  = useCallback(
    () => unreadNotifications.forEach(n => markAsRead(n.id)),
    [unreadNotifications, markAsRead],
  );
  const openDialog     = useCallback((queryId: string) => {
    setResultQueryId(queryId);
    setResultDialogOpen(true);
  }, []);
  const closeDialog    = useCallback(() => {
    setResultDialogOpen(false);
    setResultQueryId(null);
  }, []);

  // Entry point for every "Voir les résultats" click (panel button, toast button): if the
  // notification carries a workspace different from the one the user is currently on, ask for
  // confirmation before switching — the count/export result only makes sense once the backend
  // calls (query result, workspace-scoped data) are made under the matching workspace header.
  const requestOpenDialog = useCallback((queryId: string, workspace?: string) => {
    const isOnSameWorkspace = !workspace
      || !selectedWorkspace?.volumeName
      || normalizeWorkspaceId(workspace) === normalizeWorkspaceId(selectedWorkspace.volumeName);
    if (isOnSameWorkspace) {
      openDialog(queryId);
      return;
    }
    setPendingRedirect({ queryId, targetWorkspaceName: workspace! });
  }, [selectedWorkspace, openDialog]);

  const confirmRedirect = useCallback(() => {
    if (!pendingRedirect) return;
    const target = workspaces.find(ws => normalizeWorkspaceId(ws.volumeName) === normalizeWorkspaceId(pendingRedirect.targetWorkspaceName));
    if (target) setSelectedWorkspace(target);
    openDialog(pendingRedirect.queryId);
    setPendingRedirect(null);
  }, [pendingRedirect, workspaces, setSelectedWorkspace, openDialog]);

  const cancelRedirect = useCallback(() => setPendingRedirect(null), []);

  useEffect(() => {
    NotificationService.setNotificationCenterState(panelOpen);
  }, [panelOpen]);

  useEffect(() => {
    const onOpenCenter = () => setPanelOpen(true);
    const onOpenTab    = (i: number) => setTabValue(i);
    const onScroll     = (id: string) => {
      setTimeout(() => {
        const el = document.querySelector(`[data-notification-id="${id}"]`) as HTMLElement | null;
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.backgroundColor = alpha(highlightColor, 0.1);
        el.style.border = `2px solid ${highlightColor}`;
        setTimeout(() => { el.style.backgroundColor = ''; el.style.border = ''; }, 2000);
      }, 200);
    };
    const onOpenDialog = (queryId: string, workspace?: string) => requestOpenDialog(queryId, workspace);

    NotificationService.on('open-notification-center',     onOpenCenter);
    NotificationService.on('open-notification-center-tab', onOpenTab);
    NotificationService.on('scroll-to-notification',       onScroll);
    NotificationService.on('open-query-result-dialog',     onOpenDialog);

    return () => {
      NotificationService.removeListener('open-notification-center',     onOpenCenter);
      NotificationService.removeListener('open-notification-center-tab', onOpenTab);
      NotificationService.removeListener('scroll-to-notification',       onScroll);
      NotificationService.removeListener('open-query-result-dialog',     onOpenDialog);
    };
  }, [requestOpenDialog, highlightColor]);

  useEffect(() => {
    if (!panelOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Element;
      if (
        !t.closest('[role="dialog"]') &&
        !panelRef.current?.contains(t) &&
        !bellRef.current?.contains(t)
      ) {
        closePanel();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [panelOpen, closePanel]);

  return (
    <>
      <NotificationBellButton
        unreadCount={unreadCount}
        isLoading={isLoading}
        darkMode={topbarTheme === 'dark'}
        buttonRef={bellRef}
        onClick={togglePanel}
      />

      <Slide direction="left" in={panelOpen}>
        <Paper
          ref={panelRef}
          elevation={8}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'fixed',
            top: 70, right: 20,
            width: 340,
            maxHeight: '65vh',
            zIndex: 1300,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <NotificationPanelHeader
            unreadCount={unreadCount}
            tabValue={tabValue}
            onTabChange={setTabValue}
            onClose={closePanel}
            onMarkAllAsRead={markAllAsRead}
          />

          <NotificationList
            notifications={notifications}
            unreadNotifications={unreadNotifications}
            tabValue={tabValue}
            isRead={isRead}
            onMarkAsRead={markAsRead}
            onArchive={archiveNotification}
            onOpenDialog={requestOpenDialog}
          />
        </Paper>
      </Slide>

      <QueryResultDialog
        open={resultDialogOpen}
        onClose={closeDialog}
        queryId={resultQueryId}
      />

      <WorkspaceRedirectDialog
        open={pendingRedirect !== null}
        targetWorkspaceName={pendingRedirect?.targetWorkspaceName ?? ''}
        onAccept={confirmRedirect}
        onCancel={cancelRedirect}
      />
    </>
  );
};

export default NotificationBell;
